import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { CauseBlocageDossier } from '@logaia_users/logaia-dto';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { CollaborateurService } from '@shared/services/cabinet/collaborateur.service';
import { DossierService } from '@shared/services/cabinet/dossier.service';
import { NotificationService } from '../../ui/notification/notification.service';
import { Deconnexion } from '../session';
import { dossierIdDeLaRoute, pageArrivee } from '../page-arrivee';
import { PARAM_CODE_ACCES, URL_LISTE_DOSSIERS } from '../chemins';
import { MSG_CODE_ACCES_INCORRECT, MSG_DOSSIER_NON_AUTORISE } from '../droits';

/**
 * Ouverture d'un dossier (GET Dossier/{id}/IsDossierLibre, en-têtes du dossier demandé) :
 * - refusée (message) si le back le dit bloqué, par exemple pendant une clôture ;
 * - 432, dossier protégé sans code ou avec un code incorrect : liste des dossiers, fenêtre du code d'accès ouverte
 *   (collaborateur) ;
 * - 401 : dossier non autorisé, ou session perdue. Le collaborateur relit sa fiche (GET Collaborateur/ByCode) : si
 *   elle répond, la session est valide et le dossier est refusé (message, liste des dossiers, sans déconnexion) ;
 *   sinon l'intercepteur d'auth déconnecte comme pour tout 401.
 * Correctif par rapport à l'ancien : un dossier ouvert par adresse tapée ne déconnecte plus et demande son code.
 */
export const dossierLibreGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const toast = inject(NotificationService);
  const dossierService = inject(DossierService);
  const refus = refusDossier();
  const id = dossierIdDeLaRoute(route);
  return dossierService.isLibre(id).pipe(
    map((res) => {
      if (!res) {
        return true;
      }
      switch (res.causeBlocage) {
        case CauseBlocageDossier._CLOTURE:
          toast.erreur('Clôture en cours.');
          break;
        default:
          toast.erreur('Ouverture impossible.');
          break;
      }
      return false;
    }),
    catchError((err: unknown) => refus(err, id)),
  );
};

/**
 * Traitement d'un refus du back à l'ouverture d'un dossier (voir dossierLibreGuard). Doit être appelé dans un
 * contexte d'injection ; la fonction rendue peut être appelée plus tard.
 */
function refusDossier(): (err: unknown, id: number) => Observable<boolean | UrlTree> {
  const router = inject(Router);
  const toast = inject(NotificationService);
  const dossierService = inject(DossierService);
  const collaborateurService = inject(CollaborateurService);
  const deconnexion = inject(Deconnexion);
  const arrivee = pageArrivee();

  return (err, id) => {
    const statut = err instanceof HttpErrorResponse ? err.status : 0;
    if (statut === 432) {
      if (!LOGAIA_COLLAB_BUILD || !collaborateurService.isCollab) {
        return of(arrivee ? router.parseUrl(arrivee) : false);
      }
      if (dossierService.aCodeAcces(id)) {
        toast.erreur(MSG_CODE_ACCES_INCORRECT);
      }
      return of(router.createUrlTree([URL_LISTE_DOSSIERS], { queryParams: { [PARAM_CODE_ACCES]: id } }));
    }
    if (statut === 401) {
      const code = LOGAIA_COLLAB_BUILD && collaborateurService.isCollab ? collaborateurService.collab?.codeCollab : undefined;
      if (!code) {
        // Client : ses dossiers autorisés sont vérifiés avant (accesDossierGuard), un 401 signifie une session perdue.
        deconnexion.sessionPerdue(true);
        return of(false);
      }
      return collaborateurService.getByCode(code).pipe(
        map(() => {
          toast.erreur(MSG_DOSSIER_NON_AUTORISE);
          return router.parseUrl(URL_LISTE_DOSSIERS);
        }),
        // Session perdue : déconnexion par l'intercepteur d'auth.
        catchError(() => of(false)),
      );
    }
    return throwError(() => err);
  };
}
